import React, { useEffect, useState } from "react";
import { Box, Typography, useMediaQuery } from "@mui/material";
import {
  CustomSectionTitle,
  CustomTextInput,
  CustomTwoColInputGroup,
  CustomCheckbox,
  FixedActionBar,
  CustomFormNavigationGuard,
  CustomModal,
  CustomButton,
} from "@ippon/shared-components";
import { RequireIcon } from "@ippon/shared-assets";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "@/store";
import {
  fetchDetailedEmployee,
  updateDetailedEmployee,
} from "@/store/employeeSlice";
import { fetchDetailedClient } from "@/store/clientSlice";
import { useNavigate, useParams } from "react-router-dom";
import { getClientID, getEmployeeID, removeSecureCookie } from "@/utils/authUtils";
import { useCan } from "@/hooks/useCan";
import { useGlobalToast } from "@/hooks/useGlobalToast";
import { useOwnAccountRole } from "@/hooks/useOwnAccountRole";

type FormValues = {
  last_name: string;
  first_name: string;
  last_name_kana: string;
  first_name_kana: string;
  mail_address: string;
  role: "admin" | "general";
  // アカウントロール（auth_roles tier=account）
  account_role: "member" | "admin" | "owner";
  is_active: boolean;
  service_ids: string[];
};

// アカウントロールの選択肢（auth_roles tier=account の label と一致させる）
const ACCOUNT_ROLE_OPTIONS = [
  { value: "owner", label: "オーナー" },
  { value: "admin", label: "管理者" },
  { value: "member", label: "一般" },
];

// アカウントロールのランク（auth_roles tier=account の rank と一致させる。大きいほど上位）
const ACCOUNT_ROLE_RANK: Record<string, number> = { member: 10, admin: 20, owner: 30 };

const EmployeeUpdate: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width:600px)");
  const { employee_id } = useParams();
  const { showToast } = useGlobalToast();
  const [isUpdating, setIsUpdating] = useState(false);
  const [initialValues, setInitialValues] = useState<FormValues | null>(null);
  // シート購入確認ダイアログ（残席0のサービスを新たに追加した更新時に表示）
  const [openPurchaseModal, setOpenPurchaseModal] = useState(false);
  const [pendingUpdateData, setPendingUpdateData] = useState<FormValues | null>(
    null
  );
  const can = useCan();
  const canUpdateEmployee = can("employee:edit");
  const loggedInEmployeeId = getEmployeeID();
  const isOwnProfile = employee_id === loggedInEmployeeId;
  // 各フィールドの編集権限
  // 基本情報(氏名等)は本人も編集可。
  const canEditBasicInfo = canUpdateEmployee;
  // アカウントロール(会社の身分)は本人不可（自己昇格防止・SPEC）。owner も最上位ゆえロックで詰まない。
  const canEditAccountRole = canUpdateEmployee && !isOwnProfile;
  // サービス割当(参加)・製品ロールは本人可。付与権限は employee:edit(=owner/admin)で担保し、
  // 昇格の上限は BE(assertNotEscalating)が account 権限アンカーで判定する。
  const canEditServices = canUpdateEmployee;
  const canEditServiceRole = canUpdateEmployee;

  // ログイン中の自分自身のアカウントロール。自分より高いロールは選べないようにする
  const { accountRole: ownAccountRole, loading: loadingOwnAccountRole } =
    useOwnAccountRole();
  const ownAccountRoleRank = ownAccountRole
    ? ACCOUNT_ROLE_RANK[ownAccountRole] ?? 0
    : 0;
  const allowedAccountRoleOptions = loadingOwnAccountRole
    ? ACCOUNT_ROLE_OPTIONS
    : ACCOUNT_ROLE_OPTIONS.filter(
        (opt) => (ACCOUNT_ROLE_RANK[opt.value] ?? 0) <= ownAccountRoleRank
      );

  const clientId = getClientID();

  const { data: employeeData } = useSelector(
    (state: any) => state.employees.detailed
  );

  const { data: clientData } = useSelector(
    (state: any) => state.clients.detailed
  );

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
    watch,
    setError,
    clearErrors,
  } = useForm<FormValues>({
    mode: "onChange",
    defaultValues: {
      last_name: "",
      first_name: "",
      last_name_kana: "",
      first_name_kana: "",
      mail_address: "",
      role: "general",
      account_role: "member",
      is_active: true,
      service_ids: [],
    },
  });

  const formValues = watch();

  useEffect(() => {
    reset({
      last_name: "",
      first_name: "",
      last_name_kana: "",
      first_name_kana: "",
      mail_address: "",
      role: "general",
      account_role: "member",
      is_active: true,
      service_ids: [],
    });
    setInitialValues(null);
    if (employee_id) {
      void dispatch(fetchDetailedEmployee(employee_id));
    }
    if (clientId) {
      void dispatch(fetchDetailedClient(clientId));
    }
  }, [dispatch, employee_id, clientId]);

  useEffect(() => {
    if (
      employeeData &&
      Object.keys(employeeData).length > 0 &&
      employeeData.id === employee_id
    ) {
      const serviceIds =
        employeeData.assigned_services?.map((s: any) => s.service.id) || [];

      const init: FormValues = {
        last_name: employeeData.last_name || "",
        first_name: employeeData.first_name || "",
        last_name_kana: employeeData.last_name_kana || "",
        first_name_kana: employeeData.first_name_kana || "",
        mail_address: employeeData.mail_address || "",
        role: employeeData.is_admin ? "admin" : "general",
        account_role: employeeData.account_role || "member",
        is_active: employeeData.is_active !== false,
        service_ids: serviceIds,
      };
      reset(init);
      setInitialValues(init);
    }
  }, [employeeData, reset, employee_id]);

  // Validate service_ids when it changes
  useEffect(() => {
    if (canEditServices) {
      if (!formValues.service_ids || formValues.service_ids.length === 0) {
        setError("service_ids", {
          type: "manual",
          message: "利用サービスを選択してください",
        });
      } else {
        clearErrors("service_ids");
      }
    } else {
      clearErrors("service_ids");
    }
  }, [formValues.service_ids, canEditServices, setError, clearErrors]);

  // 初期値と現在値を比較して変更があるかチェック
  const hasFormChanged = (): boolean => {
    if (!initialValues) return false;

    // 配列の比較用ヘルパー関数
    const arraysEqual = (a: string[], b: string[]) => {
      if (a.length !== b.length) return false;
      const sortedA = [...a].sort();
      const sortedB = [...b].sort();
      return sortedA.every((val, idx) => val === sortedB[idx]);
    };

    return (
      formValues.last_name !== initialValues.last_name ||
      formValues.first_name !== initialValues.first_name ||
      formValues.last_name_kana !== initialValues.last_name_kana ||
      formValues.first_name_kana !== initialValues.first_name_kana ||
      formValues.mail_address !== initialValues.mail_address ||
      formValues.role !== initialValues.role ||
      formValues.account_role !== initialValues.account_role ||
      formValues.is_active !== initialValues.is_active ||
      !arraysEqual(formValues.service_ids || [], initialValues.service_ids || [])
    );
  };

  const isFormChanged = hasFormChanged();

  // 新たに追加したサービス（初期状態に無く、今回チェックした）に
  // 残席0（契約あり）が含まれるか＝席の新規購入が発生するか
  const willPurchaseSeatForAdded = (serviceIds: string[]): boolean => {
    const initialIds = initialValues?.service_ids || [];
    const addedIds = serviceIds.filter((sid) => !initialIds.includes(sid));
    const services = clientData?.client_services ?? [];
    return services.some(
      (cs: any) => addedIds.includes(cs.service.id) && cs.free_seats === 0
    );
  };

  // 更新の実処理（確認不要時の直接更新と、購入確認ダイアログの「更新」で共用）
  const doUpdate = async (data: FormValues) => {
    try {
      setOpenPurchaseModal(false);
      setPendingUpdateData(null);
      setIsUpdating(true);
      await new Promise((r) => setTimeout(r, 50));

      const payload = {
        client_id: clientId,
        last_name: data.last_name,
        first_name: data.first_name,
        last_name_kana: data.last_name_kana,
        first_name_kana: data.first_name_kana,
        mail_address: data.mail_address,
        role: data.role,
        account_role: data.account_role,
        is_active: data.is_active,
        service_ids: data.service_ids,
      };

      const id = employeeData?.id || employee_id;
      const res = await dispatch(
        updateDetailedEmployee({ id, payload }) as any
      );
      if (res?.type?.endsWith("/fulfilled")) {
        // アカウント情報更新時はサービスキャッシュをクリア
        removeSecureCookie("my_use_service");
        await new Promise((r) => setTimeout(r, 800));
        setIsUpdating(false);
        showToast({ type: "success", message: "更新が完了しました" });
        navigate("/setting/account");
      } else {
        await new Promise((r) => setTimeout(r, 800));
        setIsUpdating(false);
        showToast({ type: "error", message: "更新に失敗しました" });
      }
    } catch (e) {
      setIsUpdating(false);
      showToast({ type: "error", message: "更新に失敗しました" });
    }
  };

  const onSubmit = async (data: FormValues) => {
    if (!canUpdateEmployee) {
      showToast({ type: "error", message: "更新権限がありません" });
      return;
    }
    // 残席0のサービスを新規追加する場合は購入確認ダイアログを挟む
    if (willPurchaseSeatForAdded(data.service_ids || [])) {
      setPendingUpdateData(data);
      setOpenPurchaseModal(true);
      return;
    }
    await doUpdate(data);
  };

  return (
    <CustomFormNavigationGuard
      hasFormChanged={isFormChanged}
      isSubmitting={isUpdating}
    >
      {() => (
        <>
        <Box
          pb={3}
          pt={{ xs: 2, sm: 3 }}
          component="form"
          onSubmit={handleSubmit(onSubmit)}
        >
          <CustomSectionTitle title="プロフィール" size="large" />

          <Box className={`${isMobile ? "sp" : ""}`} sx={{ mt: 2 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                maxWidth: "600px",
                width: "100%",
              }}
            >
              <CustomTwoColInputGroup
                label="氏名"
                firstName="last_name"
                lastName="first_name"
                placeholderOne="佐藤"
                placeholderTwo="太郎"
                register={register as any}
                errors={errors as any}
                isRequired
                isModalInput={true}
                inputWidth="100%"
                inputWidthSp="100%"
                disabled={!canEditBasicInfo}
                sx={{
                  "& input:disabled, & .MuiInputBase-input.Mui-disabled, & .MuiOutlinedInput-input.Mui-disabled": {
                    backgroundColor: "rgb(245, 245, 245) !important",
                    color: "rgb(62, 62, 62) !important",
                    WebkitTextFillColor: "rgb(62, 62, 62) !important",
                    borderRadius: "8px !important",
                  },
                  "& .MuiInputBase-root.Mui-disabled, & .MuiOutlinedInput-root.Mui-disabled": {
                    backgroundColor: "rgb(245, 245, 245) !important",
                    borderRadius: "8px !important",
                    overflow: "hidden !important",
                    opacity: "1 !important",
                    "&:hover": {
                      backgroundColor: "rgb(245, 245, 245) !important",
                      opacity: "1 !important",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#D9D9D9 !important",
                      borderRadius: "8px !important",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#D9D9D9 !important",
                      borderRadius: "8px !important",
                    },
                  },
                  "& input:disabled:hover": {
                    backgroundColor: "rgb(245, 245, 245) !important",
                    opacity: "1 !important",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderRadius: "8px !important",
                  },
                }}
              />
              <CustomTwoColInputGroup
                label="フリガナ"
                firstName="last_name_kana"
                lastName="first_name_kana"
                placeholderOne="サトウ"
                placeholderTwo="タロウ"
                register={register as any}
                errors={errors as any}
                patternFirstName={{ value: /^[ァ-ヶー\s　]*$/, message: "カタカナのみとなります" }}
                patternLastName={{ value: /^[ァ-ヶー\s　]*$/, message: "カタカナで入力して下さい" }}
                inputWidth="100%"
                inputWidthSp="100%"
                disabled={!canEditBasicInfo}
                sx={{
                  "& input:disabled, & .MuiInputBase-input.Mui-disabled, & .MuiOutlinedInput-input.Mui-disabled": {
                    backgroundColor: "rgb(245, 245, 245) !important",
                    color: "rgb(62, 62, 62) !important",
                    WebkitTextFillColor: "rgb(62, 62, 62) !important",
                    borderRadius: "8px !important",
                  },
                  "& .MuiInputBase-root.Mui-disabled, & .MuiOutlinedInput-root.Mui-disabled": {
                    backgroundColor: "rgb(245, 245, 245) !important",
                    borderRadius: "8px !important",
                    overflow: "hidden !important",
                    opacity: "1 !important",
                    "&:hover": {
                      backgroundColor: "rgb(245, 245, 245) !important",
                      opacity: "1 !important",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#D9D9D9 !important",
                      borderRadius: "8px !important",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#D9D9D9 !important",
                      borderRadius: "8px !important",
                    },
                  },
                  "& input:disabled:hover": {
                    backgroundColor: "rgb(245, 245, 245) !important",
                    opacity: "1 !important",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderRadius: "8px !important",
                  },
                }}
              />
              <Box sx={{ mb: 2 }}>
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontWeight: 700,
                    mb: 1,
                    color: "#3e3e3e",
                  }}
                >
                  アカウント権限
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    flexWrap: "wrap",
                    gap: 2,
                  }}
                >
                  {allowedAccountRoleOptions.map((option) => (
                    <Box
                      key={option.value}
                      sx={{ display: "flex", alignItems: "center", gap: 1 }}
                    >
                      <CustomCheckbox
                        checked={formValues.account_role === option.value}
                        onChange={() => {
                          if (!canEditAccountRole) return;
                          setValue(
                            "account_role",
                            option.value as FormValues["account_role"],
                            { shouldDirty: true }
                          );
                        }}
                        size="small"
                        disabled={!canEditAccountRole}
                      />
                      <Box
                        component="label"
                        sx={{
                          fontSize: "14px",
                          color: "#333",
                          cursor: canEditAccountRole ? "pointer" : "default",
                          userSelect: "none",
                        }}
                        onClick={() => {
                          if (!canEditAccountRole) return;
                          setValue(
                            "account_role",
                            option.value as FormValues["account_role"],
                            { shouldDirty: true }
                          );
                        }}
                      >
                        {option.label}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Box
                  sx={{
                    fontSize: "12px",
                    fontWeight: 700,
                    mb: 1,
                    color: "#3e3e3e",
                  }}
                >
                  サービス権限
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <CustomCheckbox
                    checked={formValues.role === "admin"}
                    onChange={() => {
                      if (!canEditServiceRole) return;
                      const currentRole = formValues.role;
                      const newRole = currentRole === "admin" ? "general" : "admin";
                      setValue("role", newRole, { shouldDirty: true });
                    }}
                    size="small"
                    disabled={!canEditServiceRole}
                  />
                  <Box
                    component="label"
                    sx={{
                      fontSize: "14px",
                      color: "#333",
                      cursor: canEditServiceRole ? "pointer" : "default",
                      userSelect: "none",
                    }}
                    onClick={() => {
                      if (!canEditServiceRole) return;
                      const currentRole = formValues.role;
                      const newRole = currentRole === "admin" ? "general" : "admin";
                      setValue("role", newRole, { shouldDirty: true });
                    }}
                  >
                    管理者
                  </Box>
                </Box>
              </Box>


              <Box sx={{ mb: 2 }}>
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontWeight: 700,
                    mb: 1,
                    color: "#3e3e3e",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  利用サービス
                  <span style={{ display: "flex", marginLeft: "4px" }}>
                    <RequireIcon />
                  </span>
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    flexWrap: "wrap",
                    gap: 2,
                  }}
                >
                  {clientData?.client_services?.map((cs: any) => (
                    <Box
                      key={cs.service.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <CustomCheckbox
                        checked={(formValues.service_ids || []).includes(cs.service.id)}
                        onChange={() => {
                          if (!canEditServices) return;
                          const currentIds = formValues.service_ids || [];
                          const newIds = currentIds.includes(cs.service.id)
                            ? currentIds.filter((id) => id !== cs.service.id)
                            : [...currentIds, cs.service.id];
                          setValue("service_ids", newIds, { shouldDirty: true });
                        }}
                        disabled={!canEditServices}
                        size="small"
                      />
                      <Box
                        component="label"
                        sx={{
                          fontSize: "14px",
                          color: "#333",
                          cursor: canEditServices ? "pointer" : "default",
                          userSelect: "none",
                        }}
                        onClick={() => {
                          if (!canEditServices) return;
                          const currentIds = formValues.service_ids || [];
                          const newIds = currentIds.includes(cs.service.id)
                            ? currentIds.filter((id) => id !== cs.service.id)
                            : [...currentIds, cs.service.id];
                          setValue("service_ids", newIds, { shouldDirty: true });
                        }}
                      >
                        {cs.service.name}
                      </Box>
                      {typeof cs.free_seats === "number" && (
                        <Typography
                          sx={{
                            fontSize: "12px",
                            fontWeight: 700,
                            color: cs.free_seats === 0 ? "#FF9800" : "#666",
                            whiteSpace: "nowrap",
                          }}
                        >
                          残りシート: {cs.free_seats}
                          {cs.free_seats === 0 ? "（新規購入）" : ""}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>
              <CustomTextInput
                label="メールアドレス"
                name="mail_address"
                type="email"
                placeholder="taro@example.com"
                register={register}
                errors={errors}
                isRequired
                inputWidth="100%"
                inputWidthSp="100%"
                disabled={!canEditBasicInfo}
                sx={{
                  "& input:disabled, & .MuiInputBase-input.Mui-disabled, & .MuiOutlinedInput-input.Mui-disabled": {
                    backgroundColor: "rgb(245, 245, 245) !important",
                    color: "rgb(62, 62, 62) !important",
                    WebkitTextFillColor: "rgb(62, 62, 62) !important",
                    borderRadius: "8px !important",
                  },
                  "& .MuiInputBase-root.Mui-disabled, & .MuiOutlinedInput-root.Mui-disabled": {
                    backgroundColor: "rgb(245, 245, 245) !important",
                    borderRadius: "8px !important",
                    overflow: "hidden !important",
                    opacity: "1 !important",
                    "&:hover": {
                      backgroundColor: "rgb(245, 245, 245) !important",
                      opacity: "1 !important",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#D9D9D9 !important",
                      borderRadius: "8px !important",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#D9D9D9 !important",
                      borderRadius: "8px !important",
                    },
                  },
                  "& input:disabled:hover": {
                    backgroundColor: "rgb(245, 245, 245) !important",
                    opacity: "1 !important",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderRadius: "8px !important",
                  },
                }}
              />
            </Box>
          </Box>

          {canUpdateEmployee && (
            <FixedActionBar
              submitLabel="更新"
              isLoading={isUpdating}
              disabled={!isFormChanged || isUpdating || Object.keys(errors).length > 0}
            />
          )}
        </Box>

        {/* シート購入確認ダイアログ（残席0のサービスを新規追加した更新時） */}
        <CustomModal
          title="シート購入の確認"
          openModal={openPurchaseModal}
          handleCloseModal={() => setOpenPurchaseModal(false)}
          modalType="deleteModal"
          bodyText={
            "新規でシートが購入されますがよろしいでしょうか？\n（契約期間は契約内容によります）"
          }
        >
          <Box sx={{ display: "flex", gap: 2, justifyContent: "center", mt: 2 }}>
            <CustomButton
              label="戻る"
              buttonCategory="cancelModal"
              onClick={() => setOpenPurchaseModal(false)}
            />
            <CustomButton
              label="更新"
              buttonCategory="submit"
              type="button"
              isLoading={isUpdating}
              disabled={isUpdating}
              onClick={() => {
                if (pendingUpdateData) void doUpdate(pendingUpdateData);
              }}
              sx={{ width: "50% !important" }}
            />
          </Box>
        </CustomModal>
        </>
      )}
    </CustomFormNavigationGuard>
  );
};

export default EmployeeUpdate;
